import { format, startOfToday } from 'date-fns'
import { api } from '../api/api'
import db, { getSyncWindowStartDate, getSyncWindowEndDate, pruneOldRecords, UNSYNCED_VISIT_STATUSES, VISIT_PHOTO_TYPES } from '../db/db'
import { attachInvitationQrValidity } from '../common/utilities/invitationValidations'

/**
 * Guard that prevents concurrent sync runs.
 * If a sync is already in progress, `runSync` returns false immediately.
 * @type {boolean}
 */
let isSyncing = false

/**
 * Set by `withRetry` the instant ANY request in the current push/pull cycle
 * receives a 401 (session expired/invalid — see `docs/local-first.md`, the
 * backend's `devise_token_auth` tokens expire after 2 weeks regardless of
 * activity). Consumed and reset by `runSync`/`runPoll` in their `finally`
 * block, i.e. only once the *entire* cycle — including every concurrent
 * request in `pushPendingVisits`'s `Promise.all` — has fully settled.
 *
 * Esas 2 semanas son política deliberada de las cuentas de recepción, no la
 * del tenant: CorporateService no hereda `token_lifespan` ni
 * `max_number_of_devices` de la configuración de empleados, porque un tenant
 * con vigencia de 15 minutos dejaría el kiosko inoperable. El `expiry` además
 * queda congelado en el login (`change_headers_on_each_request = false`), así
 * que el vencimiento llega aunque el kiosko se use a diario — de ahí el aviso
 * anticipado en `useSyncEngine.js`.
 *
 * Levantar este flag no cierra la sesión por sí solo: el listener de
 * `useSyncEngine.js` confirma antes contra el servidor, porque este camino
 * corre cada minuto sin intervención y un 401 aislado no prueba que la sesión
 * esté muerta.
 *
 * This two-step (flag now, act later) design is deliberate: dispatching the
 * `lobby:auth-expired` event immediately from inside `withRetry` could fire
 * while OTHER visits in the same `Promise.all` batch still have a request in
 * flight. The listener (`useSyncEngine.js`) reacts to that event by logging
 * out, which runs `clearLocalDatabase()` — a destructive operation that
 * clears `db.visits`. If that ran concurrently with an in-flight sibling
 * request that's about to mark itself `synced`, the late write would become
 * a silent no-op (the record was already swept into quarantine), leaving an
 * accepted visit stuck as `pending` and risking a duplicate on the next
 * resync. Deferring the event until the whole cycle settles avoids this.
 * @type {boolean}
 */
let authExpiredThisCycle = false

/**
 * Emits `lobby:auth-expired` if any request during the just-finished cycle
 * hit a 401, then resets the flag. Must be called from a `finally` block in
 * `runSync`/`runPoll`, AFTER `pushPendingVisits`/`pullChanges` have fully
 * settled (success or failure), so every Dexie write for this cycle is
 * already applied and consistent before any listener reacts by logging out.
 */
function flushAuthExpiredFlag() {
  if (!authExpiredThisCycle) return
  authExpiredThisCycle = false
  window.dispatchEvent(new CustomEvent('lobby:auth-expired'))
}

// ---------------------------------------------------------------------------
// Retry helper
// ---------------------------------------------------------------------------

/**
 * Executes `fn` up to `maxAttempts` times, waiting `baseDelay * 2^attempt` ms
 * between retries (exponential backoff). Only retries on network/timeout
 * errors — 4xx responses are NOT retried because the server has explicitly
 * rejected the request.
 *
 * @param {() => Promise<any>} fn          Async function to execute
 * @param {object}  [opts]
 * @param {number}  [opts.maxAttempts=3]   Maximum number of attempts
 * @param {number}  [opts.baseDelay=1000]  Base delay in ms
 * @param {boolean} [opts.retryUnavailable=true] `false` en los GET: un 503/504
 *   o un timeout significa que el backend está saturado, y reintentar en el
 *   acto solo le suma carga. El poll ya se espacia solo (backoff en
 *   `useSyncEngine.js`). Los POST/PUT/DELETE sí reintentan: llevan cambios del
 *   usuario.
 * @returns {Promise<any>}
 * @throws  Rethrows the last error when all attempts are exhausted
 */
async function withRetry(fn, { maxAttempts = 3, baseDelay = 1000, retryUnavailable = true } = {}) {
  let lastErr
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (err) {
      const status = err?.response?.status

      // 401 = the session itself is invalid/expired — retrying with the same
      // dead token is pointless. Flag it for the caller's cycle-level check
      // (see `authExpiredThisCycle`) instead of treating it as a normal 4xx
      // rejection, and rethrow immediately.
      if (status === 401) {
        authExpiredThisCycle = true
        throw err
      }

      // 4xx = server-side rejection — no point retrying
      if (status && status >= 400 && status < 500) throw err

      if (!retryUnavailable && (status === 503 || status === 504 || isTimeoutError(err))) throw err

      lastErr = err
      const delay = baseDelay * Math.pow(2, attempt)
      console.warn(
        `[SyncEngine] Intento ${attempt + 1}/${maxAttempts} fallido. ` +
        `Reintentando en ${delay}ms…`,
        err.message
      )
      await new Promise(r => setTimeout(r, delay))
    }
  }
  throw lastErr
}

// Timeouts por petición del ciclo de sync. `axios.create()` no tiene timeout,
// y una petición colgada retendría el lock `lobby-sync` de todas las pestañas.
// Van aquí y no en el cliente para no afectar OCR, login y el resto de la app.
export const SYNC_GET_TIMEOUT_MS = 30_000
export const SYNC_PUSH_TIMEOUT_MS = 30_000
export const SYNC_PHOTO_UPLOAD_TIMEOUT_MS = 120_000

/** GET con `withRetry`, sin reintentar 503/504 ni timeouts (ver `retryUnavailable`). */
function getWithRetry(endpoint, config) {
  return withRetry(
    () => api.get(endpoint, { timeout: SYNC_GET_TIMEOUT_MS, ...config }),
    { retryUnavailable: false },
  )
}

/**
 * `true` si axios cortó la petición por `timeout`. En axios 0.24 llega como
 * `ECONNABORTED` con "timeout of Nms exceeded" (`ETIMEDOUT` solo con
 * `transitional.clarifyTimeoutError`). Un `ECONNABORTED` de "Request aborted"
 * no es timeout.
 */
function isTimeoutError(err) {
  return err?.code === 'ETIMEDOUT' || /timeout/i.test(err?.message || '')
}

/**
 * `true` si el error indica que el backend está saturado o no responde a
 * tiempo (5xx o timeout de axios). `useSyncEngine.js` lo usa para espaciar el
 * poll. Un "Network Error" sin respuesta no cuenta: suele ser el kiosko sin
 * red, y de eso se encarga el detector de conexión.
 *
 * @param {any} err
 * @returns {boolean}
 */
export function isServerUnavailableError(err) {
  const status = err?.response?.status
  if (status >= 500) return true
  return isTimeoutError(err)
}

// ---------------------------------------------------------------------------
// Lock entre pestañas
// ---------------------------------------------------------------------------

export const SYNC_LOCK_NAME = 'lobby-sync'

/** Lo devuelve `withSyncLock` cuando `ifAvailable` y otra pestaña tiene el lock. */
export const SYNC_LOCK_UNAVAILABLE = Symbol('lobby-sync-lock-unavailable')

/**
 * Ejecuta `fn` con el Web Lock `lobby-sync`, para que dos pestañas (o dos
 * ventanas del kiosko) no sincronicen a la vez contra el backend.
 *
 * Solo se pide en los puntos de entrada (`runSync`, `runPoll`, el sync de
 * `LoadPage`, `syncVisitsForBuilding`), nunca dentro de `syncEntity` ni de los
 * pulls: Web Locks no es reentrante y `runSync` se bloquearía a sí mismo.
 *
 * `ifAvailable` solo lo usa el poll periódico: si otra pestaña ya está
 * sincronizando, ese ciclo sobra. El resto espera su turno, porque saltarse
 * la descarga inicial o la de un cambio de edificio dejaría el kiosko vacío.
 *
 * Sin la API (navegadores viejos, jsdom) ejecuta `fn` directamente.
 *
 * @template T
 * @param {() => Promise<T>} fn
 * @param {{ ifAvailable?: boolean }} [opts]
 * @returns {Promise<T|typeof SYNC_LOCK_UNAVAILABLE>}
 */
export async function withSyncLock(fn, { ifAvailable = false } = {}) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks?.request) return fn()
  return locks.request(SYNC_LOCK_NAME, { ifAvailable }, lock => (lock ? fn() : SYNC_LOCK_UNAVAILABLE))
}

// ---------------------------------------------------------------------------
// Programación de los pulls que no corren en cada poll
// ---------------------------------------------------------------------------

/** Cada cuánto `pullTodayInvitations` hace una descarga completa en vez de incremental. */
export const TODAY_FULL_PULL_INTERVAL_MS = 10 * 60 * 1000
/** Cada cuánto `runPoll` incluye el pull incremental de la ventana de invitaciones. */
export const INVITATIONS_WINDOW_POLL_INTERVAL_MS = 5 * 60 * 1000
/** Cada cuánto `runPoll` refresca `/visits/comes/all`. */
export const COMES_FROM_POLL_INTERVAL_MS = 10 * 60 * 1000

// Pull de invitaciones de hoy: checkpoint propio, tamaño de página y tope de
// páginas por llamada (5,000 invitaciones; lo que sobre sigue en el próximo ciclo).
const TODAY_CHECKPOINT_KEY = 'invitations_today'
const TODAY_PAGE_LIMIT = 100
const TODAY_MAX_PAGES = 50

// Marcas en memoria: viven lo que vive la pestaña. `null` = todavía no en esta
// sesión de la app, así que lo primero que corra hace el pull.
let lastTodayFullPullAt = null
let lastWindowPullAt = null
let lastComesPullAt = null

function isDue(lastAt, intervalMs) {
  return lastAt == null || Date.now() - lastAt >= intervalMs
}

/** Olvida las marcas de arriba (tests). */
export function resetSyncSchedule() {
  lastTodayFullPullAt = null
  lastWindowPullAt = null
  lastComesPullAt = null
}

// ---------------------------------------------------------------------------
// Push
// ---------------------------------------------------------------------------

/**
 * Converts any parseable date value to a local-timezone ISO 8601 string with
 * UTC offset (e.g. "2026-01-14T12:10:10-06:00"). This is the format the
 * /visits/batch API expects for `entry` and `exit` fields.
 *
 * Returns `undefined` when the input is falsy or not a valid date so that
 * optional fields (e.g. `exit`) are simply omitted from the payload.
 *
 * @param   {string|Date|null|undefined} dateInput
 * @returns {string|undefined}
 */
function toLocalISOString(dateInput) {
  if (!dateInput) return undefined
  const date = new Date(dateInput)
  if (isNaN(date.getTime())) return undefined

  const pad = n => String(n).padStart(2, '0')
  const offsetMins = -date.getTimezoneOffset()
  const sign = offsetMins >= 0 ? '+' : '-'
  const absOffset = Math.abs(offsetMins)
  const offsetH = pad(Math.floor(absOffset / 60))
  const offsetM = pad(absOffset % 60)

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${offsetH}:${offsetM}`
  )
}

/**
 * Builds the payload object for a single visit to send to the server.
 *
 * - `create` events: include all known fields so the server has a complete record.
 * - `update` events: include ONLY the fields stored in `visit._pendingChanges`
 *   (set by EditDialog / exitConfirmation when writing to Dexie). This avoids
 *   overwriting server-side fields that were not touched locally.
 *
 * In both cases entry/exit times are converted to local-timezone ISO 8601.
 *
 * @param   {object} visit  Dexie visit record
 * @returns {object}        API-ready payload
 */
function buildVisitPayload(visit) {
  const isCreate = !visit.id

  if (isCreate) {
    return {
      uuid:           visit.uuid,
      event:          'create',
      first_name:     visit.first_name,
      last_name_one:  visit.last_name_one,
      last_name_two:  visit.last_name_two,
      identification: visit.identification,
      entry_time:     toLocalISOString(visit.entry_time),
      exit_time:      toLocalISOString(visit.exit_time),
      destination_id: visit.destination_id,
      comes_from:     visit.comes_from,
      notes:          visit.notes,
      has_laptop:     visit.has_laptop,
      laptop_serie:   visit.laptop_serie,
      card_number:    visit.card_number,
      license_plate:  visit.license_plate,
      visit_type_id:  visit.visit_type_id,
      visit_host_id:  visit.visit_host_id,
    }
  }

  // Update: only send the fields that actually changed, mapped to API names.
  const changes = visit._pendingChanges || {}
  const payload = { uuid: visit.uuid, event: 'update' }

  for (const [key, value] of Object.entries(changes)) {
    // Convert date fields to local-timezone ISO; pass everything else through as-is
    payload[key] = (key === 'entry_time' || key === 'exit_time')
      ? toLocalISOString(value)
      : value
  }

  return payload
}

// Tras este numero de CICLOS de sync consecutivos en los que el push de una
// misma visita agota los reintentos de `withRetry` (red caida o 5xx), se deja
// de reintentar automaticamente y se marca `failed` con un motivo generico.
// Esto evita que un bug puntual del backend en UN registro (p.ej. una
// excepcion no controlada del lado del servidor al procesar esa visita en
// particular) bloquee la cola de sincronizacion para siempre — el usuario ve
// el estado "failed" con su motivo en Visits.js / OfflineAudit y puede
// reintentar manualmente o escalarlo a soporte.
const MAX_PUSH_ATTEMPTS = 5

// Tope del top-N de `comes_from` que se replica localmente (ver
// `pullComesFrom` mas abajo). Ahora que `VisitAutoComplete` busca siempre en
// `db.comes_from` (nunca contra `visits/comes/:prefix` online, que no tiene
// limite), se sube de 500 a 2000 para reducir el hueco de recall en origenes
// poco frecuentes. Exportado para que `LoadPage.js` use el mismo valor en su
// sync inicial en vez de depender del default del backend.
export const COMES_FROM_LOCAL_LIMIT = 2000

/**
 * Pushes all visits with `syncStatus: 'pending'` to the server.
 *
 * Cada visita se envia en su PROPIA request a `/visits/batch` (arreglo de un
 * solo elemento) en vez de agruparlas todas en una sola llamada. Esto aisla
 * fallos: si el backend falla al procesar una visita en particular (error de
 * validacion, o incluso una excepcion no controlada del servidor), el resto
 * de visitas pendientes en la cola no se ven afectadas y pueden sincronizar
 * con normalidad. Antes, un fallo de ESE tipo en una sola visita revertia (o
 * dejaba pendiente) el batch completo, dejando visitas sanas atascadas junto
 * a la problematica.
 *
 * @returns {Promise<boolean>} `true` si alguna visita quedó escrita en Dexie
 *   (`synced`, `failed` o un intento más). `false` si la cola estaba vacía
 *   o ninguna fila cambió (p. ej. un 401 que la deja `pending`).
 */
async function pushPendingVisits() {
  const pending = await db.visits
    .where('syncStatus').equals('pending')
    .toArray()

  if (!pending.length) return false

  const wrote = await Promise.all(pending.map(pushSingleVisit))
  return wrote.some(Boolean)
}

/**
 * Pushes a single pending visit and resolves its resulting `syncStatus`.
 *
 * - Network error / 5xx (after `withRetry`'s internal backoff is exhausted):
 *   stays `pending` and increments `pushAttempts`, UNLESS this was already
 *   the `MAX_PUSH_ATTEMPTS`-th consecutive failing cycle for this visit, in
 *   which case it's marked `failed` with a generic `syncError` so it stops
 *   being retried silently forever.
 * - 4xx at the HTTP level (e.g. auth): the server explicitly rejected the
 *   request outright — marked `failed` immediately, no point waiting.
 * - 200/207 with a per-item rejection in the response body (validation
 *   errors, "visita no encontrada", etc.): marked `failed`, and the
 *   backend's message is persisted as `syncError` so the user (and the
 *   offline audit dashboard) can see *why* it was rejected.
 * - Success: marked `synced` so the subsequent pull can overwrite it with
 *   the full server record (real id, server-normalised fields, etc.).
 *   Without this, the pull's "skip pending" guard would leave it stuck as
 *   `pending` forever even though the server already accepted it.
 *
 * @param {object} visit  Dexie visit record (must include `uuid`)
 * @returns {Promise<boolean>} `true` si esta visita se escribió en Dexie
 */
async function pushSingleVisit(visit) {
  let res
  try {
    res = await withRetry(() => api.post(
      '/visits/batch',
      { visits: [buildVisitPayload(visit)] },
      { timeout: SYNC_PUSH_TIMEOUT_MS },
    ))
  } catch (err) {
    const status = err?.response?.status

    // 401 = the session is invalid/expired, not a rejection of THIS visit in
    // particular. Marking it `failed` would be misleading (it looks like a
    // permanent validation error) and would leave it stuck forever, since
    // `failed` visits are never purged. Leave it `pending` untouched — once
    // the user logs back in (see `withRetry`'s `authExpiredThisCycle` flag
    // and the `lobby:auth-expired` listener in `useSyncEngine.js`), the next
    // `runSync()` will push it again normally.
    if (status === 401) return false

    const attempts = (visit.pushAttempts || 0) + 1
    const isClientRejection = status >= 400 && status < 500

    if (isClientRejection) {
      await db.visits.where('uuid').equals(visit.uuid).modify({
        syncStatus: 'failed',
        syncError: `El servidor rechazó esta visita (HTTP ${status}).`,
        pushAttempts: attempts,
      })
      return true
    }

    if (attempts >= MAX_PUSH_ATTEMPTS) {
      console.warn(`[SyncEngine] Push de uuid=${visit.uuid} agotó ${attempts} intentos, se marca como failed:`, err.message)
      await db.visits.where('uuid').equals(visit.uuid).modify({
        syncStatus: 'failed',
        syncError: `No se pudo sincronizar tras ${attempts} intentos (${err.message}). Contacte soporte.`,
        pushAttempts: attempts,
      })
    } else {
      console.warn(`[SyncEngine] Push de uuid=${visit.uuid} fallido (intento ${attempts}/${MAX_PUSH_ATTEMPTS}):`, err.message)
      await db.visits.where('uuid').equals(visit.uuid).modify({ pushAttempts: attempts })
    }
    return true
  }

  // Response shape: { created, updated, deleted, errors: [{ index, details|error }] }
  // (see Api::VisitsController#batch). Since each request carries exactly one
  // visit, `index` is always 0 — there's no need to correlate by uuid.
  const { errors = [] } = res.data || {}
  if (errors.length) {
    const message = errors
      .map(e => (Array.isArray(e.details) ? e.details.join(', ') : e.error))
      .filter(Boolean)
      .join('; ') || 'Rechazado por el servidor'
    await db.visits.where('uuid').equals(visit.uuid).modify({ syncStatus: 'failed', syncError: message, pushAttempts: 0 })
    return true
  }

  await db.visits.where('uuid').equals(visit.uuid).modify({ syncStatus: 'synced', syncError: null, pushAttempts: 0 })
  return true
}

function buildInvitationPayload(invitation) {
  return {
    first_name: invitation.first_name,
    last_name_one: invitation.last_name_one,
    last_name_two: invitation.last_name_two,
    identification: invitation.identification,
    email: invitation.email,
    invitation_date: invitation.date || invitation.invitation_date,
    invitation_time: invitation.invitation_time,
    comes_from: invitation.comes_from,
    company_id: invitation.company_id,
    duration: invitation.duration,
    building_id: invitation.building_id,
    destination_id: invitation.destination_id,
    uuid: invitation.uuid,
    ...(invitation.license_plate ? { license_plate: invitation.license_plate } : {}),
    ...(invitation.notes ? { notes: invitation.notes } : {}),
    ...(invitation.host_notes ? { host_notes: invitation.host_notes } : {}),
    ...(invitation.reason ? { reason: invitation.reason } : {}),
  }
}

function buildInvitationRangePayload(invitation) {
  const invitationDate = invitation.date || invitation.invitation_date

  return {
    start_date: invitationDate,
    end_date: invitationDate,
    invitation: {
      first_name: invitation.first_name,
      last_name_one: invitation.last_name_one,
      last_name_two: invitation.last_name_two,
      email: invitation.email,
      identification: invitation.identification,
      invitation_time: invitation.invitation_time,
      comes_from: invitation.comes_from,
      duration: invitation.duration,
      destination_id: invitation.destination_id,
      building_id: invitation.building_id,
      uuid: invitation.uuid,
      ...(invitation.license_plate ? { license_plate: invitation.license_plate } : {}),
      ...(invitation.reason ? { reason: invitation.reason } : {}),
      ...(invitation.notes ? { notes: invitation.notes } : {}),
      ...(invitation.host_notes ? { host_notes: invitation.host_notes } : {}),
    },
  }
}

function buildInvitationCompositeKey(invitation) {
  const normalized = value => String(value || '').trim().toLowerCase()
  return [
    normalized(invitation.first_name),
    normalized(invitation.last_name_one),
    normalized(invitation.last_name_two),
    normalized(invitation.email),
    normalized(invitation.identification),
    normalized(invitation.date || invitation.invitation_date),
    normalized(invitation.invitation_time),
  ].join('|')
}

async function markInvitationsAsFailed(invitations = [], syncError = null) {
  if (!invitations.length) return
  await Promise.all(invitations.map(invitation => (
    db.invitations.update(invitation.id, {
      syncStatus: 'failed',
      syncError,
      pushAttempts: (invitation.pushAttempts || 0) + 1,
    })
  )))
}

async function replaceLocalInvitation(localInvitation, createdInvitation) {
  await db.invitations.delete(localInvitation.id)
  await db.invitations.put(attachInvitationQrValidity({
    ...localInvitation,
    ...createdInvitation,
    id: createdInvitation.id,
    date: createdInvitation.date || createdInvitation.invitation_date || localInvitation.date,
    syncStatus: 'synced',
    syncError: null,
    pushAttempts: 0,
  }))
}

async function pushRangeInvitations(invitations) {
  let wrote = false
  for (const invitation of invitations) {
    try {
      const res = await withRetry(() => api.post(
        '/invitations/range',
        buildInvitationRangePayload(invitation),
        { timeout: SYNC_PUSH_TIMEOUT_MS },
      ))
      const createdItems = res?.data?.invitations || res?.data?.invitation || []
      const created = createdItems[0]

      if (!created?.id) {
        await db.invitations.update(invitation.id, {
          syncStatus: 'failed',
          syncError: 'El servidor no confirmó la creación de esta invitación.',
          pushAttempts: 0,
        })
        wrote = true
        continue
      }

      await replaceLocalInvitation(invitation, created)
      wrote = true
    } catch (err) {
      const status = err?.response?.status

      // 401 = the session itself is invalid/expired, not a rejection of THIS
      // invitation in particular (same reasoning as `pushSingleVisit`'s 401
      // carve-out for visits). Leave it — and every remaining invitation in
      // this loop, which would fail the exact same way with the same dead
      // token — untouched (`pending`), so it retries normally after the next
      // login instead of getting stuck `failed` forever.
      if (status === 401) return wrote

      const attempts = (invitation.pushAttempts || 0) + 1

      if (status && status >= 400 && status < 500) {
        await db.invitations.update(invitation.id, {
          syncStatus: 'failed',
          syncError: `El servidor rechazó esta invitación (HTTP ${status}).`,
          pushAttempts: attempts,
        })
        wrote = true
        continue
      }

      // Mismo umbral (`MAX_PUSH_ATTEMPTS`) y semantica que `pushSingleVisit`:
      // tras varios ciclos consecutivos fallando por red/5xx, se deja de
      // reintentar automaticamente y se marca `failed` con un motivo generico.
      if (attempts >= MAX_PUSH_ATTEMPTS) {
        console.warn(`[SyncEngine] Push de invitation id=${invitation.id} agotó ${attempts} intentos, se marca como failed:`, err.message)
        await db.invitations.update(invitation.id, {
          syncStatus: 'failed',
          syncError: `No se pudo sincronizar tras ${attempts} intentos (${err.message}). Contacte soporte.`,
          pushAttempts: attempts,
        })
        wrote = true
        continue
      }

      console.warn(`[SyncEngine] Push de invitation id=${invitation.id} fallido (intento ${attempts}/${MAX_PUSH_ATTEMPTS}):`, err.message)
      await db.invitations.update(invitation.id, { pushAttempts: attempts })
      return true
    }
  }
  return wrote
}

async function pushBatchInvitations(invitations) {
  if (!invitations.length) return false

  try {
    const payload = invitations.map(buildInvitationPayload)
    const res = await withRetry(() => api.post(
      '/invitations/batch',
      { invitations: payload },
      { timeout: SYNC_PUSH_TIMEOUT_MS },
    ))
    const created = Array.isArray(res?.data?.invitations) ? res.data.invitations : []
    const errors = Array.isArray(res?.data?.errors) ? res.data.errors : []

    const createdByKey = new Map()
    for (const item of created) {
      const key = item.uuid || buildInvitationCompositeKey(item)
      if (!createdByKey.has(key)) createdByKey.set(key, [])
      createdByKey.get(key).push(item)
    }

    const matchedIds = new Set()

    await db.transaction('rw', db.invitations, async () => {
      for (const invitation of invitations) {
        const key = invitation.uuid || buildInvitationCompositeKey(invitation)
        const matches = createdByKey.get(key) || []
        const createdInvitation = matches.shift()

        if (!createdInvitation?.id) {
          await db.invitations.update(invitation.id, {
            syncStatus: 'failed',
            syncError: 'El servidor no confirmó la creación de esta invitación.',
            pushAttempts: 0,
          })
          continue
        }

        matchedIds.add(invitation.id)
        await replaceLocalInvitation(invitation, createdInvitation)
      }
    })

    if (errors.length) {
      const unmatched = invitations.filter(invitation => !matchedIds.has(invitation.id))
      const message = errors
        .map(e => (Array.isArray(e.details) ? e.details.join(', ') : e.error))
        .filter(Boolean)
        .join('; ') || 'Rechazado por el servidor'
      await markInvitationsAsFailed(unmatched, message)
    }
    return true
  } catch (err) {
    const status = err?.response?.status

    // 401 = the session itself is invalid/expired — leave every pending
    // batch invitation untouched (`pending`) instead of marking them
    // `failed`, so they retry after the next login. See the same carve-out
    // in `pushSingleVisit`/`pushRangeInvitations`.
    if (status === 401) return false

    if (status && status >= 400 && status < 500) {
      await markInvitationsAsFailed(invitations, `El servidor rechazó este lote de invitaciones (HTTP ${status}).`)
      return true
    }

    // Mismo umbral (`MAX_PUSH_ATTEMPTS`) que `pushSingleVisit`/`pushRangeInvitations`,
    // aplicado por-invitacion (cada una conserva su propio contador de intentos
    // aunque viajen juntas en un solo request de batch).
    console.warn('[SyncEngine] Push de lote de invitaciones fallido; se reintentara luego:', err.message)
    await Promise.all(invitations.map(invitation => {
      const attempts = (invitation.pushAttempts || 0) + 1
      return db.invitations.update(invitation.id, attempts >= MAX_PUSH_ATTEMPTS
        ? {
          syncStatus: 'failed',
          syncError: `No se pudo sincronizar tras ${attempts} intentos (${err.message}). Contacte soporte.`,
          pushAttempts: attempts,
        }
        : { pushAttempts: attempts })
    }))
    return true
  }
}

async function pushPendingInvitationDeletes(invitations) {
  let wrote = false

  for (const invitation of invitations) {
    try {
      const response = await withRetry(() => api.delete(
        `/invitation/${invitation.id}`,
        { timeout: SYNC_PUSH_TIMEOUT_MS },
      ))

      if (!response?.data?.success) {
        await db.invitations.update(invitation.id, {
          syncStatus: 'failed',
          syncError: response?.data?.errors?.[0] || 'El servidor rechazó la eliminación del pre registro.',
        })
        wrote = true
        continue
      }

      await db.invitations.delete(invitation.id)
      wrote = true
    } catch (err) {
      const status = err?.response?.status
      if (status === 401) return wrote

      const attempts = (invitation.pushAttempts || 0) + 1
      const rejected = status && status >= 400 && status < 500
      await db.invitations.update(invitation.id, rejected || attempts >= MAX_PUSH_ATTEMPTS
        ? {
          syncStatus: 'failed',
          syncError: rejected
            ? `El servidor rechazó la eliminación del pre registro (HTTP ${status}).`
            : `No se pudo eliminar tras ${attempts} intentos (${err.message}). Contacte soporte.`,
          pushAttempts: attempts,
        }
        : { pushAttempts: attempts })
      wrote = true
    }
  }

  return wrote
}

async function pushPendingInvitationUpdates(invitations) {
  let wrote = false

  for (const invitation of invitations) {
    try {
      const response = await withRetry(() => api.put(
        `/invitation/${invitation.id}`,
        invitation.pendingUpdatePayload,
        { timeout: SYNC_PUSH_TIMEOUT_MS },
      ))

      if (!response?.data?.success) {
        await db.invitations.update(invitation.id, {
          syncStatus: 'failed',
          syncError: response?.data?.errors?.[0] || 'El servidor rechazó la actualización del pre registro.',
        })
        wrote = true
        continue
      }

      const { pendingUpdatePayload, syncOperation, ...localInvitation } = invitation
      await db.invitations.put({
        ...localInvitation,
        ...response.data.invitation,
        syncStatus: 'synced',
        syncError: null,
        pushAttempts: 0,
      })
      wrote = true
    } catch (err) {
      const status = err?.response?.status
      if (status === 401) return wrote

      const attempts = (invitation.pushAttempts || 0) + 1
      const rejected = status && status >= 400 && status < 500
      await db.invitations.update(invitation.id, rejected || attempts >= MAX_PUSH_ATTEMPTS
        ? {
          syncStatus: 'failed',
          syncError: rejected
            ? `El servidor rechazó la actualización del pre registro (HTTP ${status}).`
            : `No se pudo actualizar tras ${attempts} intentos (${err.message}). Contacte soporte.`,
          pushAttempts: attempts,
        }
        : { pushAttempts: attempts })
      wrote = true
    }
  }

  return wrote
}

async function pushPendingInvitations() {
  const pending = await db.invitations
    .where('syncStatus').equals('pending')
    .toArray()

  if (!pending.length) return false

  const deleteInvitations = pending.filter(invitation => invitation.syncOperation === 'delete')
  const updateInvitations = pending.filter(invitation => invitation.syncOperation === 'update')
  const batchInvitations = pending.filter(invitation => !invitation.syncOperation && invitation.syncMode === 'batch')
  const rangeInvitations = pending.filter(invitation => !invitation.syncOperation && invitation.syncMode !== 'batch')

  const deletesWrote = await pushPendingInvitationDeletes(deleteInvitations)
  const updatesWrote = await pushPendingInvitationUpdates(updateInvitations)
  const rangeWrote = await pushRangeInvitations(rangeInvitations)
  const batchWrote = batchInvitations.length
    ? await pushBatchInvitations(batchInvitations)
    : false
  return deletesWrote || updatesWrote || rangeWrote || batchWrote
}

function dispatchPushApplied() {
  window.dispatchEvent(new CustomEvent('lobby:push-applied'))
}

// ---------------------------------------------------------------------------
// Fotos pendientes
// ---------------------------------------------------------------------------

/**
 * Sube una foto encolada en `pending_visit_photos` a su endpoint del backend.
 *
 * A diferencia del resto de los push, este NO puede ejecutarse antes del pull:
 * `Api::VisitPhotoController` resuelve la visita con `Visit.find(params[:id])`,
 * así que necesita el id numérico del servidor, y ese id solo llega a la
 * réplica local cuando el pull baja la visita ya creada. Si la visita todavía
 * no lo tiene (sigue `pending`, o su push fue rechazado), se sale sin hacer
 * request y la fila espera al próximo ciclo.
 *
 * @param {object} row Fila de `pending_visit_photos`
 */
async function pushSinglePhoto(row) {
  const config = VISIT_PHOTO_TYPES[row.type]
  if (!config) {
    await db.pending_visit_photos.delete(row.key)
    return
  }

  const visit = await db.visits.get(row.uuid)
  if (!visit) {
    // La visita ya no existe localmente (descartada o purgada): la foto no
    // tiene destino posible, así que no vale la pena conservar el blob.
    await db.pending_visit_photos.delete(row.key)
    return
  }
  if (!visit.id) return

  const formData = new FormData()
  formData.append('visit_photo[data]', row.blob, row.fileName)

  let res
  try {
    res = await withRetry(() => api.post(`${config.endpoint}/${visit.id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: SYNC_PHOTO_UPLOAD_TIMEOUT_MS,
    }))
  } catch (err) {
    const status = err?.response?.status

    // 401 = sesión inválida, no un rechazo de ESTA foto — misma lógica que
    // `pushSingleVisit`: se deja intacta para reintentar tras el próximo login.
    if (status === 401) return

    const attempts = (row.pushAttempts || 0) + 1

    if (status >= 400 && status < 500) {
      await db.pending_visit_photos.update(row.key, {
        syncStatus: 'failed',
        syncError: `El servidor rechazó esta foto (HTTP ${status}).`,
        pushAttempts: attempts,
      })
      return
    }

    if (attempts >= MAX_PUSH_ATTEMPTS) {
      console.warn(`[SyncEngine] Subida de foto ${row.key} agotó ${attempts} intentos, se marca como failed:`, err.message)
      await db.pending_visit_photos.update(row.key, {
        syncStatus: 'failed',
        syncError: `No se pudo subir tras ${attempts} intentos (${err.message}). Contacte soporte.`,
        pushAttempts: attempts,
      })
    } else {
      console.warn(`[SyncEngine] Subida de foto ${row.key} fallida (intento ${attempts}/${MAX_PUSH_ATTEMPTS}):`, err.message)
      await db.pending_visit_photos.update(row.key, { pushAttempts: attempts })
    }
    return
  }

  // El controlador responde 200 incluso cuando la validación del modelo falla,
  // distinguiéndolo solo con `success: false` (ver Api::VisitPhotoController#create).
  if (res?.data?.success === false) {
    const errors = res.data.errors
    await db.pending_visit_photos.update(row.key, {
      syncStatus: 'failed',
      syncError: (Array.isArray(errors) ? errors.join(', ') : errors) || 'Rechazado por el servidor',
      pushAttempts: 0,
    })
    return
  }

  // Guardar la metadata devuelta (`{ id, name }`) en la visita local para que
  // los consumidores que la leen por id (PrintTicketModal, DetailDialog,
  // EditDialog) puedan mostrar la foto sin esperar al siguiente pull.
  const visitPhoto = res?.data?.visit_photo
  if (visitPhoto?.id) {
    await db.visits.where('uuid').equals(row.uuid).modify({ [config.visitField]: visitPhoto })
  }

  await db.pending_visit_photos.delete(row.key)
}

/**
 * Sube todas las fotos encoladas cuya visita ya tiene id del servidor.
 *
 * Debe invocarse DESPUÉS de `pullChanges` (ver `pushSinglePhoto`). Es barata
 * cuando no hay nada que subir — un único `toArray()` sobre una tabla vacía —
 * así que se ejecuta también en cada `runPoll`, lo que le da reintentos
 * automáticos sin depender de una reconexión.
 *
 * @returns {Promise<void>}
 */
async function pushPendingPhotos() {
  const pending = await db.pending_visit_photos
    .where('syncStatus').equals('pending')
    .toArray()

  if (!pending.length) return

  await Promise.all(pending.map(pushSinglePhoto))
}

// ---------------------------------------------------------------------------
// Pull
// ---------------------------------------------------------------------------

/**
 * Broadcasts a single batch-endpoint page request/response so external
 * observers (e.g. the offline audit dashboard's checkpoint timeline) can
 * verify the pagination mechanism is actually advancing and pulling data,
 * without coupling SyncEngine to any UI code.
 *
 * @param {object} detail See callers below for the exact shape emitted.
 */
function dispatchCheckpointEvent(detail) {
  window.dispatchEvent(new CustomEvent('lobby:sync-checkpoint', {
    detail: { ...detail, timestamp: new Date().toISOString() }
  }))
}

/**
 * Downloads incremental changes for a single entity from the server.
 * Uses a `lastCheckpoint` stored in `sync_meta` to fetch only records
 * updated since the last successful sync (delta sync).
 *
 * Special handling:
 *  - `invitations`: supports soft-deletes via `_delete` flag
 *  - `visits`:      skips server data for locally-pending records to prevent
 *                   overwriting unsynchronised changes (offline-first contract)
 *  - everything else: blind upsert via `bulkPut`
 *
 * Fires `lobby:sync-checkpoint` once per page requested, so each round-trip
 * to a batch endpoint can be audited (request checkpoint, items received,
 * whether more pages remain, the new checkpoint, and how long it took).
 *
 * @param {object}      opts
 * @param {string}      opts.endpoint      API endpoint (e.g. '/visits/batch')
 * @param {Dexie.Table} opts.table         Dexie table to write into
 * @param {string}      opts.dataKey       Key inside the API response that holds the array
 * @param {string}      [opts.checkpointKey=dataKey] Key used to persist/read the checkpoint in
 *   `sync_meta` and to label dispatched events. Defaults to `dataKey`, but can be overridden
 *   so two independent pulls against the same API response shape (e.g. invitations "hoy" vs
 *   "ventana") don't clobber each other's checkpoint.
 * @param {object}      [opts.params]      Additional query params merged into the request.
 *   No debe llevar `min_updated_at` ni `after_id`: pisarían el cursor en todas las
 *   páginas y la paginación no avanzaría. Para eso está `initialCheckpoint`.
 * @param {string}      [opts.initialCheckpoint] Cursor de la primera página cuando
 *   `sync_meta` no tiene checkpoint (p. ej. la fecha de hoy en `LoadPage`). Las
 *   páginas siguientes usan el cursor que devuelve el servidor.
 */
async function syncEntity({ endpoint, table, dataKey, checkpointKey = dataKey, params = {}, initialCheckpoint }) {
  const meta = await db.sync_meta.get(checkpointKey)
  // Fall back to a distant date so the first sync fetches everything.
  // `currentCheckpoint` advances with each paginated response so subsequent
  // pages request only records newer than the last batch received — preventing
  // infinite loops when `hasMore` stays true across many pages.
  let currentCheckpoint = meta?.lastCheckpoint ?? initialCheckpoint ?? '2000-01-01'
  // `lastId` desempata registros con el mismo `updated_at`. Sin él, un segundo
  // con más filas que `limit` deja el checkpoint quieto y la bajada se corta.
  let currentAfterId = meta?.lastId ?? null
  const startCheckpoint = currentCheckpoint
  const startAfterId = currentAfterId
  let hasMore = true
  let page = 0

  while (hasMore) {
    // Snapshot into a const so the closure doesn't capture the mutable `let`
    // (satisfies no-loop-func; also makes the intent explicit).
    const checkpoint = currentCheckpoint
    const afterId = currentAfterId
    page += 1
    const startedAt = Date.now()

    let res
    try {
      res = await getWithRetry(endpoint, {
        // El cursor va después de `params` para que nada lo pise.
        params: {
          limit: 100,
          ...params,
          min_updated_at: checkpoint,
          ...(afterId != null ? { after_id: afterId } : {}),
        }
      })
    } catch (err) {
      dispatchCheckpointEvent({
        entity: checkpointKey, endpoint, page, requestCheckpoint: checkpoint,
        success: false, error: err.message, durationMs: Date.now() - startedAt,
      })
      throw err
    }

    const { data } = res
    const items = data.success ? (data[dataKey] || []) : []

    dispatchCheckpointEvent({
      entity: checkpointKey,
      endpoint,
      page,
      requestCheckpoint: checkpoint,
      success: !!data.success,
      itemsReceived: items.length,
      hasMore: !!data.hasMore,
      responseCheckpoint: data.lastCheckpoint ?? null,
      total: data.total ?? null,
      durationMs: Date.now() - startedAt,
    })

    if (!data.success) break

    if (dataKey === 'invitations') {
      for (const item of items) {
        const existing = await table.get(item.id)
        if (item._delete) {
          if (existing?.id !== undefined) await table.delete(existing.id)
        } else {
          if (existing && existing.syncStatus === 'pending') continue
          await table.put({
            ...existing,
            ...item,
            syncStatus: 'synced',
          })
        }
      }
    } else if (dataKey === 'visits') {
      for (const item of items) {
        // Use client UUID when present; fall back to stringified server id.
        // El backend (CorporateService) ya persiste y devuelve el `uuid`
        // enviado en la creacion (Api::VisitsController#create/visit_params),
        // por lo que `item.uuid` siempre deberia venir presente para visitas
        // creadas con el flujo actual.
        const uuid = item.uuid || String(item.id)
        const existing = await table.get(uuid)
        // Do NOT overwrite a locally-pending record — the client version is newer
        if (existing && existing.syncStatus === 'pending') continue
        await table.put({ ...item, uuid, syncStatus: 'synced' })
      }
    } else {
      // Simple upsert — no conflict resolution needed for reference data
      await table.bulkPut(items)
    }

    hasMore = data.hasMore ?? false

    // El cursor es (lastCheckpoint, lastId). Avanza si cambia la marca de
    // tiempo o, con la misma marca, el id. Si ninguno cambia y hasMore sigue
    // en true, la siguiente petición sería idéntica.
    const nextCheckpoint = data.lastCheckpoint || null
    const nextId = data.lastId ?? null
    const checkpointAdvanced = !!(nextCheckpoint && nextCheckpoint !== currentCheckpoint)
    const idAdvanced = nextId != null && nextId !== currentAfterId
    if (checkpointAdvanced || idAdvanced) {
      if (nextCheckpoint) currentCheckpoint = nextCheckpoint
      currentAfterId = nextId != null ? nextId : null
    } else if (hasMore) {
      console.warn(
        `[SyncEngine] ${endpoint} devolvió hasMore=true pero el cursor ` +
        `(lastCheckpoint=${data.lastCheckpoint ?? 'ausente'}, lastId=${data.lastId ?? 'ausente'}) ` +
        'no avanzó. Deteniendo paginación para evitar bucle infinito.'
      )
      hasMore = false
    }
  }

  // Persist the final checkpoint so the next sync only fetches deltas.
  // Only write if the cursor actually advanced beyond the initial value.
  if (currentCheckpoint !== startCheckpoint || currentAfterId !== startAfterId) {
    const metaRow = { entity: checkpointKey, lastCheckpoint: currentCheckpoint }
    if (currentAfterId != null) metaRow.lastId = currentAfterId
    await db.sync_meta.put(metaRow)
  }
}

// ---------------------------------------------------------------------------
// Invitaciones: pull dedicado y prioritario de "hoy" + refresco de comes_from
// ---------------------------------------------------------------------------

/**
 * Pull dedicado y prioritario de invitaciones de **hoy** (por `invitation_date`,
 * no `updated_at`). Se ejecuta en cada `runPoll`/`runSync`, independientemente
 * del checkpoint incremental de la ventana histórica: es lo único que
 * `VisitForm` necesita para autocompletar offline, así que nunca debe quedar a
 * medio camino por culpa de una paginación interrumpida.
 *
 * Pagina con `limit` + `hasMore`, sin `start_date`/`end_date` (eso es el modo
 * ventana). Un backend que ignora `limit` en modo hoy (producción hasta que
 * entre el cambio de CorporateService) responde todo con `hasMore: false`, y el
 * bucle da una sola vuelta.
 *
 * Es incremental sobre el checkpoint `invitations_today` de `sync_meta`, pero
 * hace una descarga completa (sin `min_updated_at`) cuando:
 *  - lo pide el llamador (`full: true`, p. ej. `LoadPage`: un checkpoint de la
 *    sesión anterior omitiría invitaciones de hoy actualizadas antes);
 *  - es la primera de esta sesión de la app, o pasaron
 *    `TODAY_FULL_PULL_INTERVAL_MS` desde la última completa;
 *  - no hay checkpoint guardado;
 *  - cambió el día: la fecha de algún `lastCheckpoint` de la respuesta es
 *    mayor que `serverDay`. Se compara la fecha del servidor (zona del tenant),
 *    no la del kiosko, que no conoce esa zona.
 *
 * `serverDay` (en la misma fila de `sync_meta`) es el día más alto que el
 * servidor ha mostrado en cualquier `lastCheckpoint`, con o sin filas. No se
 * compara contra la fecha del cursor: el cursor es el `updated_at` de la
 * última invitación, que suele ser de días atrás, y cada página vacía (que
 * trae la hora actual del servidor) parecería un cambio de día. Nunca baja,
 * aunque una descarga completa traiga solo filas antiguas. Si todavía no hay
 * `serverDay`, se guarda sin forzar una completa.
 *
 * Una página vacía nunca avanza el cursor: con cero filas el servidor devuelve
 * su hora actual, y una fila confirmada justo antes con un `updated_at` menor
 * quedaría fuera para siempre.
 *
 * Si una descarga completa llega a `TODAY_MAX_PAGES`, guarda el cursor y cuenta
 * como completa: el ciclo siguiente sigue incremental desde ahí (orden
 * `updated_at, id`) y trae el resto, en vez de reiniciar desde cero.
 *
 * @param {object}  [opts]
 * @param {boolean} [opts.full=false] Forzar la descarga completa.
 * @throws Relanza el error tras agotar los reintentos de `withRetry` para que
 *   el llamador (runSync/runPoll) decida cómo proceder — este pull es
 *   crítico y no debe fallar en silencio.
 */
async function pullTodayInvitations({ full = false } = {}) {
  const endpoint = '/invitations/batch'
  const meta = await db.sync_meta.get(TODAY_CHECKPOINT_KEY)
  const isFull = full || !meta?.lastCheckpoint || isDue(lastTodayFullPullAt, TODAY_FULL_PULL_INTERVAL_MS)
  const savedServerDay = meta?.serverDay ?? null

  let checkpoint = isFull ? null : meta.lastCheckpoint
  let afterId = isFull ? null : (meta.lastId ?? null)
  let serverDay = savedServerDay
  let advanced = false
  let dayChanged = false
  let completed = false
  let hitPageCap = false

  for (let page = 1; page <= TODAY_MAX_PAGES; page++) {
    const requestCheckpoint = checkpoint
    const params = {
      limit: TODAY_PAGE_LIMIT,
      ...(requestCheckpoint ? { min_updated_at: requestCheckpoint } : {}),
      ...(afterId != null ? { after_id: afterId } : {}),
    }
    const startedAt = Date.now()
    let res

    try {
      res = await getWithRetry(endpoint, { params })
    } catch (err) {
      dispatchCheckpointEvent({
        entity: TODAY_CHECKPOINT_KEY, endpoint, page, requestCheckpoint,
        success: false, error: err.message, durationMs: Date.now() - startedAt,
      })
      throw err
    }

    const { data } = res
    const items = data.success ? (data.invitations || []) : []

    dispatchCheckpointEvent({
      entity: TODAY_CHECKPOINT_KEY,
      endpoint,
      page,
      requestCheckpoint,
      success: !!data.success,
      itemsReceived: items.length,
      hasMore: !!data.hasMore,
      responseCheckpoint: data.lastCheckpoint ?? null,
      durationMs: Date.now() - startedAt,
    })

    if (!data.success) break

    await mergeTodayInvitations(items)

    // Cadenas YYYY-MM-DD: la comparación lexicográfica es cronológica.
    const responseDay = checkpointDay(data.lastCheckpoint)
    if (responseDay && (!serverDay || responseDay > serverDay)) {
      if (!isFull && savedServerDay) dayChanged = true
      serverDay = responseDay
    }

    // Página vacía: no hay cursor que tomar (ver arriba) y no puede haber más.
    if (!items.length) {
      completed = true
      break
    }

    const nextCheckpoint = data.lastCheckpoint || null
    const nextId = data.lastId ?? null
    const cursorAdvanced = (nextCheckpoint && nextCheckpoint !== checkpoint) || (nextId != null && nextId !== afterId)
    if (cursorAdvanced) {
      if (nextCheckpoint) checkpoint = nextCheckpoint
      afterId = nextId
      advanced = true
    }

    if (!data.hasMore) {
      completed = true
      break
    }
    if (!cursorAdvanced) {
      console.warn(
        `[SyncEngine] ${endpoint} (hoy) devolvió hasMore=true pero el cursor ` +
        `(lastCheckpoint=${data.lastCheckpoint ?? 'ausente'}, lastId=${data.lastId ?? 'ausente'}) ` +
        'no avanzó. Deteniendo paginación para evitar bucle infinito.'
      )
      break
    }
    if (page === TODAY_MAX_PAGES) {
      hitPageCap = true
      console.warn(
        `[SyncEngine] ${endpoint} (hoy) alcanzó el tope de ${TODAY_MAX_PAGES} páginas; ` +
        'el próximo ciclo sigue incremental desde este cursor.'
      )
    }
  }

  // Una sola fila por cursor + `serverDay`. Si el cursor no avanzó se conserva
  // el guardado: cualquier cursor anterior a ahora sigue trayendo todas las
  // invitaciones de hoy actualizadas después, así que nunca hace falta borrarlo.
  if (advanced || serverDay !== savedServerDay) {
    const metaRow = { entity: TODAY_CHECKPOINT_KEY }
    const rowCheckpoint = advanced ? checkpoint : meta?.lastCheckpoint
    const rowId = advanced ? afterId : meta?.lastId
    if (rowCheckpoint) metaRow.lastCheckpoint = rowCheckpoint
    if (rowCheckpoint && rowId != null) metaRow.lastId = rowId
    if (serverDay) metaRow.serverDay = serverDay
    await db.sync_meta.put(metaRow)
  }

  if (isFull && (completed || (hitPageCap && advanced))) lastTodayFullPullAt = Date.now()

  if (dayChanged) await pullTodayInvitations({ full: true })
}

/** Parte `YYYY-MM-DD` de un `lastCheckpoint` del servidor (zona del tenant). */
function checkpointDay(checkpoint) {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(checkpoint || '')
  return match ? match[1] : null
}

/**
 * Aplica una página de invitaciones de hoy a Dexie: borra las `_delete`, no
 * pisa las locales `pending` y elimina el gemelo `local:<uuid>` de una
 * invitación que el servidor ya creó.
 */
async function mergeTodayInvitations(items) {
  for (const item of items) {
    if (item._delete) {
      await db.invitations.delete(item.id)
      continue
    }

    const existing = await db.invitations.get(item.id)
    if (existing?.syncStatus === 'pending') continue

    await db.invitations.put({
      ...existing,
      ...item,
      syncStatus: 'synced',
    })

    if (!item.uuid) continue
    const twins = await db.invitations
      .filter(inv => inv.uuid === item.uuid && String(inv.id).startsWith('local:'))
      .toArray()
    for (const twin of twins) {
      if (twin.id !== item.id) await db.invitations.delete(twin.id)
    }
  }
}

/**
 * Refresco completo (no incremental) del top-N de `comes_from` por
 * frecuencia/recencia. Es un dataset pequeño y estable — se reemplaza por
 * completo en cada sync en vez de mantener un checkpoint, así los registros
 * que salen del top-N también desaparecen localmente.
 */
async function pullComesFrom() {
  const endpoint = '/visits/comes/all'
  const startedAt = Date.now()
  let res

  try {
    res = await getWithRetry(endpoint, { params: { limit: COMES_FROM_LOCAL_LIMIT } })
  } catch (err) {
    dispatchCheckpointEvent({
      entity: 'comes', endpoint, page: 1, requestCheckpoint: null,
      success: false, error: err.message, durationMs: Date.now() - startedAt,
    })
    throw err
  }

  const { data } = res
  const items = data.success ? (data.comes || []) : []

  dispatchCheckpointEvent({
    entity: 'comes',
    endpoint,
    page: 1,
    requestCheckpoint: null,
    success: !!data.success,
    itemsReceived: items.length,
    hasMore: false,
    responseCheckpoint: data.lastCheckpoint ?? null,
    durationMs: Date.now() - startedAt,
  })

  if (!data.success) return

  await db.transaction('rw', db.comes_from, async () => {
    await db.comes_from.clear()
    await db.comes_from.bulkPut(items)
  })
}

/**
 * Downloads recent changes for all entities.
 *
 * El pull de invitaciones de "hoy" se ejecuta primero y de forma aislada
 * (ver `pullTodayInvitations`) porque es prioritario y crítico; el resto de
 * entidades se sincroniza en paralelo. `visits`/`invitations` se acotan a la
 * ventana de relevancia (`SYNC_WINDOW_DAYS`, ver docs/local-first.md) — el
 * historial completo sigue disponible online vía búsqueda puntual indexada.
 *
 * @param {object}  [opts]
 * @param {boolean} [opts.syncEmployees=false] Pass `true` only when
 *   `enabled_visit_host` is active — employees are only needed by the
 *   VisitHostAutoComplete and syncing them otherwise wastes bandwidth.
 * @param {boolean} [opts.syncInvitationsWindow=false] Trae el histórico
 *   paginado de invitaciones dentro de la ventana ±90 días (incremental sobre
 *   `invitations_window`). `runSync` lo pide siempre; `runPoll`, cada
 *   `INVITATIONS_WINDOW_POLL_INTERVAL_MS`. El pull de "hoy" ya cubre lo
 *   crítico en cada ciclo corto.
 * @param {boolean} [opts.syncComesFrom=true] Refresca `/visits/comes/all`.
 *   `runPoll` lo pide cada `COMES_FROM_POLL_INTERVAL_MS`.
 */
async function pullChanges({ syncEmployees = false, syncInvitationsWindow = false, syncComesFrom = true } = {}) {
  // Prioritario y aislado: si esto falla, `withRetry` ya reintentó antes de
  // propagar el error, así que el resto del ciclo ni siquiera arranca.
  await pullTodayInvitations()

  const windowStart = getSyncWindowStartDate()

  const tasks = [
    syncEntity({
      endpoint: '/visits/batch',
      table: db.visits,
      dataKey: 'visits',
      params: { since_date: windowStart },
    }),
    syncEntity({
      endpoint: '/black_lists',
      table: db.black_lists,
      dataKey: 'black_lists',
    }),
  ]

  if (syncComesFrom) tasks.push(pullComesFrom())

  if (syncInvitationsWindow) {
    tasks.push(syncEntity({
      endpoint: '/invitations/batch',
      table: db.invitations,
      dataKey: 'invitations',
      checkpointKey: 'invitations_window',
      params: { start_date: windowStart, end_date: getSyncWindowEndDate() },
    }))
  }

  if (syncEmployees) {
    tasks.push(syncEntity({
      endpoint: '/employees',
      table: db.employees,
      dataKey: 'users',
    }))
  }

  await Promise.all(tasks)

  const finishedAt = Date.now()
  if (syncComesFrom) lastComesPullAt = finishedAt
  if (syncInvitationsWindow) lastWindowPullAt = finishedAt

  // Purga post-sync: mantiene la réplica local acotada a la ventana de
  // relevancia, respetando visitas pending/failed e invitaciones de hoy.
  await pruneOldRecords()
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Main sync entry point: push → pull.
 *
 * Dispatches window events so UI components can react without being coupled
 * to this module:
 *  - `lobby:sync-start`      fired immediately when sync begins
 *  - `lobby:sync-checkpoint` fired once per batch-endpoint page requested
 *                            during the pull phase (see `syncEntity`)
 *  - `lobby:push-applied`    fired after the push wrote at least one visit or
 *                            invitation, before the pull. Lists reload from
 *                            Dexie without waiting for the rest of the cycle.
 *                            Not fired when the queues were empty.
 *  - `lobby:sync-complete`   fired after a successful push+pull cycle
 *  - `lobby:sync-error`      fired if an unexpected error occurs
 *
 * @param {object}  [opts]
 * @param {boolean} [opts.syncEmployees=false] Forward to `pullChanges` —
 *   sync employees only when `enabled_visit_host` is active.
 * @returns {Promise<boolean>} `true` on success, `false` if already syncing or on error
 */
export async function runSync({ syncEmployees = false } = {}) {
  // Prevent parallel executions — the guard is reset in `finally`
  if (isSyncing) return false

  isSyncing = true
  try {
    // Espera el lock si otra pestaña está sincronizando (nunca `ifAvailable`:
    // el arranque en frío y la reconexión no se pueden saltar).
    return await withSyncLock(() => runSyncCycle({ syncEmployees }))
  } finally {
    isSyncing = false
  }
}

async function runSyncCycle({ syncEmployees }) {
  window.dispatchEvent(new CustomEvent('lobby:sync-start'))

  try {
    const visitsWritten = await pushPendingVisits()
    // Cold start/reconexión: trae también el histórico paginado de
    // invitaciones dentro de la ventana ±90 días (best-effort).
    const invitationsWritten = await pushPendingInvitations()
    if (visitsWritten || invitationsWritten) dispatchPushApplied()
    await pullChanges({ syncEmployees, syncInvitationsWindow: true })
    // Después del pull: recién ahí las visitas recién creadas tienen el id
    // numérico que exigen los endpoints de foto.
    await pushPendingPhotos()
    window.dispatchEvent(new CustomEvent('lobby:sync-complete'))
    return true
  } catch (err) {
    console.error('[SyncEngine] Error durante sync:', err)
    window.dispatchEvent(new CustomEvent('lobby:sync-error', { detail: err }))
    return false
  } finally {
    // Runs after pushPendingVisits/pullChanges have fully settled (either
    // branch above already awaited them to completion), so it's safe for
    // the listener to log out and wipe Dexie — see `flushAuthExpiredFlag`.
    flushAuthExpiredFlag()
  }
}

/**
 * Lightweight poll triggered by the background interval.
 *
 * Pulls incremental changes and retries locally-pending visits/invitations.
 * Offline creates never call `runSync` at save time, and a failed push on
 * reconnect would otherwise sit in Dexie until the next full sync. An empty
 * pending queue is a single Dexie query (no network). Photos still run after
 * the pull so newly-created visits have the numeric id those endpoints need.
 *
 * Does NOT fire `lobby:sync-start` — that event drives the "Sincronizando…"
 * toast, which should only appear for a full `runSync` cycle. If the push
 * wrote rows, fires `lobby:push-applied` so the lists reload before the pull.
 * Still fires `lobby:sync-complete` when the cycle settles.
 *
 * También es lo que se llama después de guardar una visita (VisitForm, Visits,
 * EditDialog): sube lo pendiente y baja lo de hoy sin el costo de `runSync`.
 * La ventana de invitaciones y `/visits/comes/all` entran solo cada
 * `INVITATIONS_WINDOW_POLL_INTERVAL_MS` / `COMES_FROM_POLL_INTERVAL_MS`.
 *
 * @param {object}  [opts]
 * @param {boolean} [opts.syncEmployees=false] Forward to `pullChanges`.
 * @param {boolean} [opts.ifAvailable=false] `true` solo en el poll periódico:
 *   si otra pestaña tiene el lock, este ciclo se salta en vez de esperar.
 * @returns {Promise<boolean>} `true` on success, `false` if already syncing,
 *   if the lock was taken (`ifAvailable`) or on error. El error queda en
 *   `getLastPollError()`.
 */
export async function runPoll({ syncEmployees = false, ifAvailable = false } = {}) {
  // Reuse the same guard — prevents a manual sync and a poll from running
  // at the same time, which would cause race conditions in Dexie writes.
  if (isSyncing) {
    lastPollError = null
    return false
  }

  isSyncing = true
  try {
    const result = await withSyncLock(() => runPollCycle({ syncEmployees }), { ifAvailable })
    if (result === SYNC_LOCK_UNAVAILABLE) {
      lastPollError = null
      return false
    }
    return result
  } finally {
    isSyncing = false
  }
}

/**
 * Error del último `runPoll` (o `null` si terminó bien o se saltó). El hook lo
 * usa para decidir el backoff sin cambiar el `boolean` que devuelve `runPoll`.
 */
let lastPollError = null

export function getLastPollError() {
  return lastPollError
}

async function runPollCycle({ syncEmployees }) {
  lastPollError = null
  let pushed = false
  try {
    const visitsWritten = await pushPendingVisits()
    const invitationsWritten = await pushPendingInvitations()
    if (visitsWritten || invitationsWritten) dispatchPushApplied()
    pushed = true
    await pullChanges({
      syncEmployees,
      syncInvitationsWindow: isDue(lastWindowPullAt, INVITATIONS_WINDOW_POLL_INTERVAL_MS),
      syncComesFrom: isDue(lastComesPullAt, COMES_FROM_POLL_INTERVAL_MS),
    })
    await pushPendingPhotos()
    window.dispatchEvent(new CustomEvent('lobby:sync-complete'))
    return true
  } catch (err) {
    // Polling errors are non-critical — log as warning, don't surface to user.
    // If the push already wrote `synced` into Dexie, lists still need a
    // reload: otherwise the icon stays pending until CTRL+R.
    console.warn('[SyncEngine] Poll fallido:', err.message)
    lastPollError = err
    if (pushed) {
      window.dispatchEvent(new CustomEvent('lobby:sync-complete'))
    }
    return false
  } finally {
    // See `runSync`'s finally block for why this must run only after
    // pullChanges has fully settled.
    flushAuthExpiredFlag()
  }
}

/**
 * Re-syncs visits for the current building after a building switch.
 * Clears the local visits table and the visits checkpoint first so the
 * pull downloads a full fresh set for today.
 *
 * Defense in depth: the building selector (VisitorsFilter) is expected to
 * disable itself while there are `pending`/`failed` visits or quarantined
 * records, but if that guard were ever bypassed, blindly clearing `db.visits`
 * would destroy data the server never confirmed. So — mirroring
 * `clearLocalDatabase` — any unsynced visit is moved into `quarantined_visits`
 * first; the next login flow (LoadPage + PendingVisitsModal) will offer to
 * resync or discard it.
 */
export async function syncVisitsForBuilding() {
  // Espera el lock (sin `ifAvailable`): la tabla se vacía aquí mismo, así que
  // saltarse la descarga dejaría el kiosko sin visitas.
  await withSyncLock(async () => {
    const todayStr = format(startOfToday(), 'yyyy-MM-dd')

    const unsynced = await db.visits.where('syncStatus').anyOf(UNSYNCED_VISIT_STATUSES).toArray()
    if (unsynced.length) {
      const quarantinedAt = new Date().toISOString()
      await db.quarantined_visits.bulkPut(unsynced.map(v => ({ ...v, quarantinedAt })))
    }

    await db.visits.clear()
    await db.sync_meta.delete('visits')
    await syncEntity({
      endpoint: '/visits/batch',
      table: db.visits,
      dataKey: 'visits',
      initialCheckpoint: todayStr,
      params: { since_date: getSyncWindowStartDate() },
    })
  })
}

export { syncEntity, pushPendingVisits, pushPendingInvitations, pushPendingPhotos, pullTodayInvitations, pullComesFrom, pullChanges }
